/**
 * @fileoverview Input/output schemas for the generate-custom-style-guide-v2 agent.
 *
 * This agent synthesizes a custom writing style guide from various influences.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { TemplateInfluenceProseSchema } from '@/domains/style-guides/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Flow Input Schema - accepts loaded prose objects for the agent.
 * Includes execution fields (logId, model, customPrompt) from BaseFlowInputSchema.
 *
 * Note: The hook converts template IDs to template prose objects before
 * calling executeAgent. This schema expects the loaded templates.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.selectedSliderLevels.schema)
  .extend({
    // Influences - Arrays of template prose objects
    genreTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected genre influence.'),
    writerTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected writer influence.'),
    universeTemplates: z.array(TemplateInfluenceProseSchema.nullable()).optional().describe('An array of prose objects for each selected universe influence.'),
    sourceText: z.string().optional().describe('A passage of prose to use as voice/style reference. Used by the extract-styleguide flow to feed the imported book text.'),
    antiInfluences: z.array(z.string()).optional(),

    // Guardrails
    toneKeywords: z.array(z.string()).optional(),
    recommendedPhrases: z.array(z.string()).optional(),
    customBannedPhrases: z.array(z.string()).optional(),

    // Slider values (0-4 scale)
    sexualContent: z.number().min(0).max(4).optional(),
    physicalViolence: z.number().min(0).max(4).optional(),
    emotionalViolence: z.number().min(0).max(4).optional(),
    profanity: z.number().min(0).max(4).optional(),
    narrativeDistance: z.number().min(0).max(4).optional(),
    visualSpecificity: z.number().min(0).max(4).optional(),
    syntacticComplexity: z.number().min(0).max(4).optional(),
    vocabularyRegister: z.number().min(0).max(4).optional(),
    descriptionDensity: z.number().min(0).max(4).optional(),
    pacingCadence: z.number().min(0).max(4).optional(),
    expositionIntegration: z.number().min(0).max(4).optional(),
    dialogueTexture: z.number().min(0).max(4).optional(),
    emotionalSaturation: z.number().min(0).max(4).optional(),
    atmosphericWeight: z.number().min(0).max(4).optional(),
  });

export type Input = z.infer<typeof InputSchema>;

/**
 * Hook Input type - accepts template IDs that the hook converts to template prose.
 *
 * Note: This is distinct from Input because the hook performs pre-processing
 * to load templates from IDs before calling executeAgent. Template arrays
 * here contain string IDs, which are converted to prose objects by the hook.
 */
export type HookInput = {
  /** Template IDs for genre influences (hook converts to prose) */
  genreTemplates?: string[];
  /** Template IDs for writer influences (hook converts to prose) */
  writerTemplates?: string[];
  /** Template IDs for universe influences (hook converts to prose) */
  universeTemplates?: string[];
  sourceText?: string;
  antiInfluences?: string[];

  // Guardrails
  toneKeywords?: string[];
  recommendedPhrases?: string[];
  customBannedPhrases?: string[];

  // Slider values (0-4 scale)
  sexualContent?: number;
  physicalViolence?: number;
  emotionalViolence?: number;
  profanity?: number;
  narrativeDistance?: number;
  visualSpecificity?: number;
  syntacticComplexity?: number;
  vocabularyRegister?: number;
  descriptionDensity?: number;
  pacingCadence?: number;
  expositionIntegration?: number;
  dialogueTexture?: number;
  emotionalSaturation?: number;
  atmosphericWeight?: number;
};

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output Schema - the generated style guide and metadata.
 */
export const OutputSchema = z.object({
  styleGuide: z.string().describe('The complete style guide in markdown format.'),
  meta: z.object({
    derivedTone: z.array(z.string()),
    primaryGenre: z.string(),
    contentRating: z.string(),
    pacingProfile: z.string(),
    synthesisNotes: z.string(),
  }),
});

export type Output = z.infer<typeof OutputSchema>;
